import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  SharedModule,
  StringCustomFieldConfig,
  FormInputComponent,
  NotificationService,
} from '@vendure/admin-ui/core';
import { Subject, takeUntil } from 'rxjs';
import { safeJSONParse } from '../../common/ui/utils';
import { ErpLingXingLogisticsSetting } from '../../common/ui/ui-types';

@Component({
  // no selector needed for dynamic form input
  template: `
    <div class="clr-row">
      <vdr-form-field [label]="'Logistics Provider Name'" class="clr-col-7">
        <input
          type="text"
          [formControl]="carrierCtrl"
          placeholder="Carrier name (e.g. USPS / UPS / SF Express)"
          [readonly]="readonly"
        />
      </vdr-form-field>
      <vdr-form-field [label]="'Tracking# Source'" class="clr-col-5">
        <select
          [formControl]="sourceCtrl"
          [disabled]="readonly"
          title="Tracking Source"
        >
          <option value="tracking_no">tracking_no</option>
          <option value="waybill_no">waybill_no</option>
        </select>
      </vdr-form-field>
    </div>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class ErpLingXingLogisticsSettingFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  carrierCtrl = new FormControl();
  sourceCtrl = new FormControl();

  private row: ErpLingXingLogisticsSetting;
  private destroy$ = new Subject<void>();
  private isApplyingExternalValue = false;

  constructor(
    private notificationService: NotificationService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    // Initial load
    this.applyExternalValue(this.formControl.value);

    // React to external changes (e.g. channel switch)
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        // Only apply if it's an external change, not from our own push()
        if (!this.isApplyingExternalValue) {
          this.applyExternalValue(value);
          this.cdr.markForCheck();
        }
      });
  }

  ngAfterViewInit(): void {
    this.carrierCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => {
        if (this.row.erpLogisticsCarrierName !== v) {
          this.row.erpLogisticsCarrierName = v || '';
          this.push();
        }
      });

    this.sourceCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(v => {
        if (this.row.trackingNoSource !== v) {
          this.row.trackingNoSource = (v || 'tracking_no') as 'tracking_no' | 'waybill_no';
          this.push();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyExternalValue(value: string): void {
    const defaultErpLingXingLogisticsSetting: ErpLingXingLogisticsSetting = {
      erpLogisticsCarrierName: '',
      trackingNoSource: 'tracking_no' as const,
    };

    this.row = safeJSONParse<ErpLingXingLogisticsSetting>(
      value,
      this.notificationService,
      defaultErpLingXingLogisticsSetting,
    )!;

    // Use emitEvent: false to prevent triggering our own valueChanges subscriptions
    this.carrierCtrl.setValue(this.row?.erpLogisticsCarrierName ?? '', { emitEvent: false });
    this.sourceCtrl.setValue(this.row?.trackingNoSource ?? 'tracking_no', { emitEvent: false });
  }

  private push(): void {
    this.isApplyingExternalValue = true;
    this.formControl.setValue(JSON.stringify(this.row));
    this.formControl.markAsDirty();
    this.isApplyingExternalValue = false;
  }
}